import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { S } from './PlanningRoutePanel.styles';
import { PlanningRoutePanelProps } from './PlanningRoutePanel.interfaces';
import { FormattedMessage, useIntl } from 'react-intl';
import { CloseButton } from 'shared/Buttons/CloseButton/CloseButton';
import { PinCard } from './PinCard/PinCard';
import { PlannedRoutePinInfo } from 'commonTypes/GuideUse/guideGeo';
import { ListChildComponentProps } from 'react-window';
import { getDistanceUnitSelector, getElevationUnitSelector } from 'store/Common';
import { useSelector } from 'react-redux';
import { EditButton } from 'shared/Buttons/EditButton/EditButton';
import { SaveButton } from 'shared/Buttons/SaveButton/SaveButton';
import { WaiterPopup } from 'shared/WaiterAnimation/WaiterPopup';
import { EditableMultilineText } from 'shared/EditableMultilineText/EditableMultilineText';
import { getTotalGrade } from 'utils/plannedRoutesHelper';
import { ReverseButton } from 'shared/Buttons/ReverseButton/ReverseButton';

type ItemData = { pinsInfo: PlannedRoutePinInfo[] | null };

const PlanningRoutePanelComponent: React.FC<PlanningRoutePanelProps> = ({
  plannedRoute,
  isSavingPlannedRoutes,
  onPinRemoveClick,
  onSaveClick,
  onRenameClick,
  onReverseClick,
  onChangeDescription,
  onCloseClick
}) => {
  
  const handleSave = () => {
      onSaveClick(plannedRoute)
  };

  const handleRename = () => {
      onRenameClick(plannedRoute)
  };

  const handleReverse = () => {
      onReverseClick(plannedRoute)
  };
  

  let intl = useIntl()
  const actualDistanceUnit = useSelector(getDistanceUnitSelector);
  const actualElevationUnit = useSelector(getElevationUnitSelector);

  const itemData = useMemo<ItemData>(() => ({ pinsInfo: plannedRoute.pinsInfo ?? null }), [plannedRoute.pinsInfo]);

  const Row = React.memo(
    ({ index, style, data }: ListChildComponentProps<PlannedRoutePinInfo[] | null>) => {
      if (data != null) {
        const pin:PlannedRoutePinInfo = data[index]
        return (
          <div style={style}>
            {pin && <PinCard pin={pin} onPinRemoveClick={onPinRemoveClick} />}
          </div>
        );
      }
      return null;
  },
  (prev, next) =>
      prev.data != null && next.data != null &&
      prev.index === next.index &&
      prev.data[prev.index] === next.data[prev.index] &&
      prev.style === next.style
  );
 
  const getItemSize = useCallback((index: number) => {
      if (itemData.pinsInfo){
          const pinInfo = itemData?.pinsInfo[index];
          const lines = Math.ceil((pinInfo?.trailName?.length ?? 0)/ 43); // adjust 40 based on font/container width
          return 40 + lines * 31; // base + per-line height
      }
      return 0;
  }, [itemData.pinsInfo]);

  const isRouteChanged = ((plannedRoute.isNew || plannedRoute.isEdited) && plannedRoute.pins.length > 1);

  return (
    <S.Container>
      <S.Header>
        <S.Title><FormattedMessage id="planned_route"/></S.Title>
        <S.Buttons>
            {isRouteChanged && <SaveButton onClick={handleSave}/> }
            {itemData?.pinsInfo && itemData.pinsInfo.length > 1 && <ReverseButton onClick={handleReverse}/> }
            <CloseButton onClick={onCloseClick} />
        </S.Buttons>
      </S.Header>
      <S.DetailSection>
        <S.SubHeader>
          <S.Name>
              {plannedRoute.routeName ? plannedRoute.routeName : `[${intl.formatMessage({id:"untitled"})}]`}
          </S.Name>
          <EditButton onClick={handleRename} />
        </S.SubHeader>
        <S.SubHeader>
          <S.Description>
            <EditableMultilineText 
              initialText={plannedRoute.routeDescription} 
              textPlaceholder={`${intl.formatMessage({id:"description"})}`}
              onChange={(value) => onChangeDescription(value)}/>
          </S.Description>
        </S.SubHeader>
        <S.TotalLength>
            {((plannedRoute.trail?.trackLength ?? 0) / actualDistanceUnit.valueInMeters).toFixed(1)}{' '}
            <FormattedMessage id={actualDistanceUnit.name}/>
        </S.TotalLength>
        <S.Li>
            <FormattedMessage id="total_asc"/>:{' '}
            {((plannedRoute.trail?.totalAscent ?? 0) / actualElevationUnit.valueInMeters).toFixed(0)}{' '}
            <FormattedMessage id={actualElevationUnit.shortName}/>
        </S.Li>
        <S.Li>
            <FormattedMessage id="total_desc"/>:{' '}
            {((plannedRoute.trail?.totalDescent ?? 0) / actualElevationUnit.valueInMeters).toFixed(0)}{' '}
            <FormattedMessage id={actualElevationUnit.shortName}/>
        </S.Li>
        <S.Li>
            <FormattedMessage id="total_grade"/>:{' '}
            {getTotalGrade(plannedRoute, actualDistanceUnit, actualElevationUnit).toFixed(0)}{' '}
            <FormattedMessage id={actualElevationUnit.shortName}/>
        </S.Li>
      </S.DetailSection>

      {itemData?.pinsInfo !== null ? (
        <S.ListContainer>
          <AutoSizer>
            {({ height, width }) => (
              <List
                height={height}
                itemCount={itemData?.pinsInfo ? itemData.pinsInfo.length : 0}
                itemSize={getItemSize}
                width={width}
                itemData={itemData?.pinsInfo}>
                {Row}
              </List>
            )}
          </AutoSizer>
        </S.ListContainer>
      ) : (
        <WaiterPopup open={true} />
      )}
      {isSavingPlannedRoutes && <WaiterPopup open={true} />}
    </S.Container>
  );
};

export const PlanningRoutePanel = memo(PlanningRoutePanelComponent, isEqual);
